import React, { ReactElement, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import {
  useCreateSession,
  useInvalidateSession,
  useUpdateSession,
  useSessions,
} from '../../../hooks/useSessions';
import { useRidersById } from '../../../hooks/useRiders';
import { useUnsavedChangesWarning } from '../../../hooks/useUnsavedChangesWarning';
import {
  HiOutlineCheckCircle,
  HiOutlineXMark,
  HiOutlinePencil,
  HiOutlineExclamationTriangle,
} from 'react-icons/hi2';
import toast from 'react-hot-toast';
import { SessionAnalysisType, SessionCatalog } from '../../../types/SessionAnalysisType';
import Button from '../../../components/Button';
import ButtonList from '../../../components/ButtonList';
import FormInput from '../../../components/FormInput';
import DatePickerField from '../../../components/DatePickerField';
import FormEntry from '../../../components/FormEntry';
import Skeleton from '../../../components/Skeleton';
// Combined component with radio button selection
import NewSessionCalibrationParameters from './NewSessionParameters';
import NewSessionCalibrationConfig from './NewSessionCalibrationConfig';
import NewSessionRouteSelector from './NewSessionRouteSelector';
import SessionOptions from './options/SessionOptions';
import SectionHeader from '../../../components/SectionHeader';
import DetailCard from '../../../components/DetailCard';
import { parse } from 'date-fns';
import { useSessionNavigation } from '../../../hooks/useSessionNavigation';
import SessionNavigation from '../../../components/SessionNavigation';
import NimbusLiveChip from './NimbusLiveChip';
import { getSessionRunTimeRange, sessionHasSettlingRuns, RUN_PROCESSING_LABEL } from '../../../utils/sessionTime';
import {
  NEW_SESSION_FORM_KEY,
  NEW_SESSION_ROUTE_KEY,
  clearStash,
  readStash,
} from '../../RouteSelection/routePickerConstants';

interface SessionFormProps {
  sessionId: string;
  session: any;
  isSessionFetching: boolean;
  isSessionPending: boolean;
  isNew: boolean;
}

// Move SessionCards outside of SessionForm to prevent recreation
const SessionCards = React.memo(
  ({
    formMethods,
    ridersById,
    getRiderName,
    isNew,
    updateSession,
    showRiderWarning,
    advancedOptions,
    session,
    onSummaryUpdate,
  }: {
    formMethods: any;
    ridersById: any;
    getRiderName: (rider_id: string) => string;
    isNew: boolean;
    updateSession: any;
    showRiderWarning?: boolean;
    advancedOptions?: React.ReactNode;
    session?: any;
    onSummaryUpdate?: () => Promise<void>;
  }) => {
    const [isEditingSummary, setIsEditingSummary] = useState(false);
    const [summaryContent, setSummaryContent] = useState('');
    const [isSavingSummary, setIsSavingSummary] = useState(false);

    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [notesContent, setNotesContent] = useState('');
    const [isSavingNotes, setIsSavingNotes] = useState(false);

    // Convert <br> tags to newlines for editing
    const htmlToTextarea = (html: string) => html.replace(/<br\s*\/?>/gi, '\n');

    // Update summaryContent when session changes (only when not editing)
    React.useEffect(() => {
      if (!isEditingSummary) {
        setSummaryContent(htmlToTextarea(session?.summary || ''));
      }
    }, [session?.summary, isEditingSummary]);

    // Update notesContent when session changes (only when not editing)
    React.useEffect(() => {
      if (!isEditingNotes) {
        setNotesContent(htmlToTextarea(session?.notes || ''));
      }
    }, [session?.notes, isEditingNotes]);

    const save = async (update: any) => {
      await updateSession.mutateAsync(update);
    };

    const hasSummary = session?.summary && session.summary.length > 0;
    const hasNotes = session?.notes && session.notes.length > 0;

    const handleSaveSummary = async () => {
      setIsSavingSummary(true);
      try {
        // Convert newlines to <br> tags for HTML display
        const formattedContent = summaryContent.replace(/\r?\n/g, '<br>');
        await save({ summary: formattedContent });
        if (onSummaryUpdate) {
          await onSummaryUpdate();
        }
        setIsEditingSummary(false);
      } catch (e) {
        console.error(e);
      } finally {
        setIsSavingSummary(false);
      }
    };

    const handleCancelSummary = () => {
      setSummaryContent(htmlToTextarea(session?.summary || ''));
      setIsEditingSummary(false);
    };

    const handleSaveNotes = async () => {
      setIsSavingNotes(true);
      try {
        // Convert newlines to <br> tags for HTML display
        const formattedContent = notesContent.replace(/\r?\n/g, '<br>');
        await save({ notes: formattedContent });
        if (onSummaryUpdate) {
          await onSummaryUpdate();
        }
        setIsEditingNotes(false);
      } catch (e) {
        console.error(e);
      } finally {
        setIsSavingNotes(false);
      }
    };

    const handleCancelNotes = () => {
      setNotesContent(htmlToTextarea(session?.notes || ''));
      setIsEditingNotes(false);
    };

    return (
      <div className="cr-flex cr-flex-col cr-gap-0 cr-pb-4">
        {/* Cards container — wrapper shrinks to content width on lg+ so child cards can match */}
        <div className="lg:cr-w-max">
        <div className="cr-flex cr-flex-col cr-gap-2 lg:cr-flex-row lg:cr-gap-4">
          {/* The Rider and Description inputs below carry !cr-font-medium to override the cr-font-light
              that FormInput hardcodes. These cards sit inside a .font-label wrapper, which puts the
              surrounding text at weight 500, and Poppins is only loaded at 400/500/600/700 — so
              font-light's 300 fell back to the 400 face and each field read visibly lighter than its
              own read-only value. Most visible on the New Session page, where all three edit at once.
              Date uses DatePickerField, which sets its own weight and does not go through FormInput. */}
          <DetailCard key="date-card" label="Date" canEdit={!isNew}>
            {(isEdit, setIsSaving) =>
              isEdit || isNew ? (
                <DatePickerField
                  fieldName="date"
                  required="Date is required"
                  customClass="!cr-h-[42px] !cr-w-full lg:!cr-w-44 cr-mx-auto"
                  enableDebouncedSave={!isNew}
                  debouncedSaveFn={async (value: string) => {
                    await save({ date: value });
                  }}
                  setIsSaving={setIsSaving}
                />
              ) : (
                parse(formMethods.getValues('date'), 'yyyy-MM-dd', new Date()).toLocaleDateString()
              )
            }
          </DetailCard>
          <DetailCard
            key="rider-card"
            label="Rider"
            canEdit={!isNew}
            className={showRiderWarning ? 'cr-animate-pulse cr-ring-2 cr-ring-orange-400 cr-ring-offset-2' : ''}
          >
            {(isEdit, setIsSaving) =>
              isEdit || isNew ? (
                <FormInput
                  fieldName="rider_id"
                  required="Rider is required"
                  options={ridersById && Array.from(ridersById.values()).filter(
                    (rider: any) => !rider.non_current || rider.id === formMethods.getValues('rider_id'),
                  )}
                  optionLabelFn={(rider: any) => rider.nickname}
                  emptyOption={isNew ? 'Select a Rider' : undefined}
                  customClass="!cr-h-[42px] !cr-w-full lg:!cr-w-44 cr-mx-auto !cr-font-medium"
                  enableDebouncedSave={!isNew}
                  debouncedSaveFn={async (value: string) => {
                    await save({ rider_id: value });
                  }}
                  setIsSaving={setIsSaving}
                />
              ) : (
                getRiderName(formMethods.getValues('rider_id'))
              )
            }
          </DetailCard>
          <DetailCard
            key="description-card"
            label="Description"
            canEdit={true}
          >
            {(isEdit, setIsSaving) =>
              isEdit || isNew ? (
                <FormInput
                  fieldName="description"
                  required="Description is required"
                  rows={2}
                  defaultValue="Test Session"
                  customClass="!cr-h-[42px] !cr-w-full lg:!cr-w-44 cr-mx-auto !cr-font-medium"
                  enableDebouncedSave={!isNew}
                  debouncedSaveFn={async (value: string) => {
                    await save({ description: value });
                  }}
                  setIsSaving={setIsSaving}
                />
              ) : (
                formMethods.getValues('description')
              )
            }
          </DetailCard>
        </div>
        {!isNew && (() => {
          const range = getSessionRunTimeRange(session);
          const analysisLabel = SessionCatalog[session?.analysis_type as SessionAnalysisType]?.[0];
          const cardClass =
            'cr-mt-2 cr-w-full cr-rounded-lg cr-bg-white cr-px-3 cr-py-2 ' +
            'cr-shadow-md cr-text-sm cr-italic cr-font-main';
          return (
            <div className={`${cardClass} cr-text-gray-700`}>
              Runs:{' '}
              {range ? (
                <>
                  {range.start} - {range.end}
                  {range.tzLabel && (
                    <span className="cr-ml-1 cr-text-gray-500">({range.tzLabel})</span>
                  )}
                </>
              ) : (
                // No timestamped run yet. "Planned" is only true if there is
                // nothing in flight — otherwise the header would contradict the
                // "Processing…" rows in the table right below it.
                <span className="cr-text-gray-500">
                  {sessionHasSettlingRuns(session) ? RUN_PROCESSING_LABEL : 'Planned'}
                </span>
              )}
              {analysisLabel && (
                <span className="cr-ml-2 cr-text-gray-500">• {analysisLabel}</span>
              )}
              <NimbusLiveChip sessionId={session?.id} />
            </div>
          );
        })()}
        </div>

        {/* Summary card - only show if summary exists and has content */}
        {!isNew && hasSummary && (
          <div className="cr-mt-4">
            <div
              className={`cr-group cr-relative cr-w-full cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md ${!isEditingSummary ? 'cr-cursor-pointer' : ''}`}
              onClick={() => !isEditingSummary && setIsEditingSummary(true)}
            >
              {!isEditingSummary && (
                <div className="cr-absolute cr-right-2 cr-top-2 cr-opacity-0 cr-transition-opacity cr-duration-200 group-hover:cr-opacity-100">
                  <HiOutlinePencil className="cr-text-xl cr-text-blue-900" />
                </div>
              )}
              <label className="font-montserrat cr-mb-2 cr-block cr-text-lg cr-font-bold cr-uppercase cr-text-blue-900">
                Summary
              </label>
              {isEditingSummary ? (
                <div className="cr-flex cr-flex-col cr-gap-2">
                  <textarea
                    className="cr-min-h-[150px] cr-w-full cr-rounded cr-border cr-border-gray-300 cr-p-2 cr-font-main cr-text-sm cr-text-blue-900 focus:cr-border-blue-500 focus:cr-outline-none"
                    value={summaryContent}
                    onChange={(e) => setSummaryContent(e.target.value)}
                    placeholder="Enter summary content (HTML supported)..."
                  />
                  <div className="cr-flex cr-justify-end cr-gap-2">
                    <button
                      onClick={handleCancelSummary}
                      className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-gray-200 cr-px-3 cr-py-1.5 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-300"
                    >
                      <HiOutlineXMark className="cr-h-4 cr-w-4" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveSummary}
                      disabled={isSavingSummary}
                      className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-blue-900 cr-px-3 cr-py-1.5 cr-text-sm cr-text-white hover:cr-bg-blue-850 disabled:cr-opacity-50"
                    >
                      <HiOutlineCheckCircle className="cr-h-4 cr-w-4" />
                      {isSavingSummary ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="cr-font-main cr-text-sm cr-text-blue-900"
                  dangerouslySetInnerHTML={{ __html: session?.summary || '' }}
                />
              )}
            </div>
          </div>
        )}

        {/* Notes card - only show if notes exists and has content */}
        {!isNew && hasNotes && (
          <div className="cr-mt-4">
            <div
              className={`cr-group cr-relative cr-w-full cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md ${!isEditingNotes ? 'cr-cursor-pointer' : ''}`}
              onClick={() => !isEditingNotes && setIsEditingNotes(true)}
            >
              {!isEditingNotes && (
                <div className="cr-absolute cr-right-2 cr-top-2 cr-opacity-0 cr-transition-opacity cr-duration-200 group-hover:cr-opacity-100">
                  <HiOutlinePencil className="cr-text-xl cr-text-blue-900" />
                </div>
              )}
              <label className="font-montserrat cr-mb-2 cr-block cr-text-lg cr-font-bold cr-uppercase cr-text-blue-900">
                Notes
              </label>
              {isEditingNotes ? (
                <div className="cr-flex cr-flex-col cr-gap-2">
                  <textarea
                    className="cr-min-h-[150px] cr-w-full cr-rounded cr-border cr-border-gray-300 cr-p-2 cr-font-main cr-text-sm cr-text-blue-900 focus:cr-border-blue-500 focus:cr-outline-none"
                    value={notesContent}
                    onChange={(e) => setNotesContent(e.target.value)}
                    placeholder="Enter notes content (HTML supported)..."
                  />
                  <div className="cr-flex cr-justify-end cr-gap-2">
                    <button
                      onClick={handleCancelNotes}
                      className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-gray-200 cr-px-3 cr-py-1.5 cr-text-sm cr-text-gray-700 hover:cr-bg-gray-300"
                    >
                      <HiOutlineXMark className="cr-h-4 cr-w-4" />
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveNotes}
                      disabled={isSavingNotes}
                      className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-bg-blue-900 cr-px-3 cr-py-1.5 cr-text-sm cr-text-white hover:cr-bg-blue-850 disabled:cr-opacity-50"
                    >
                      <HiOutlineCheckCircle className="cr-h-4 cr-w-4" />
                      {isSavingNotes ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="cr-font-main cr-text-sm cr-text-blue-900"
                  dangerouslySetInnerHTML={{ __html: session?.notes || '' }}
                />
              )}
            </div>
          </div>
        )}

        {/* Options section - show below cards on both mobile and desktop */}
        {!isNew && advancedOptions && <div>{advancedOptions}</div>}
      </div>
    );
  },
);
SessionCards.displayName = 'SessionCards';

export default function SessionForm({
  sessionId,
  session,
  isSessionFetching,
  isSessionPending,
  isNew,
}: SessionFormProps): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const invalidateSession = useInvalidateSession(sessionId);
  const { data: ridersById, isPending: isRidersPending } = useRidersById();
  const { data: allSessions } = useSessions();

  // Local state management
  const [showRiderWarning, setShowRiderWarning] = useState(false);
  const [showTestTypeWarning, setShowTestTypeWarning] = useState(false);
  const [showVelodromeWarning, setShowVelodromeWarning] = useState(false);
  const [sessionMode, setSessionModeState] = useState<'basic' | 'advanced'>(
    () => {
      const stored = sessionStorage.getItem('sessionMode');
      return stored === 'advanced' ? 'advanced' : 'basic';
    },
  );
  const setSessionMode = (mode: 'basic' | 'advanced') => {
    sessionStorage.setItem('sessionMode', mode);
    setSessionModeState(mode);
  };
  const isMissingConfigs = !session || !session.configs || session.configs.length === 0;

  const sessionBasePath = `/sessions/${sessionId}`;
  const configsPath = `${sessionBasePath}/config`;
  const onConfigsView = location.pathname === configsPath || location.pathname === sessionBasePath;
  const headerBackLink =
    !onConfigsView && !isNew
      ? { to: configsPath, text: 'Back to Configs' }
      : { to: '/sessions', text: 'Back to Sessions' };

  // Next and previous sessions (skips non-navigable singletons)
  const { nextSessionId, previousSessionId } = useSessionNavigation(sessionId, !isNew);

  // Form methods — for new sessions, rehydrate any values stashed before a
  // route-picker round trip (the navigation remounts this component). Read
  // once (lazy state init); useForm only consumes defaultValues on first
  // render anyway.
  const [restoredFormValues] = useState<Record<string, any> | null>(
    () => (isNew ? readStash<Record<string, any>>(NEW_SESSION_FORM_KEY) : null),
  );
  const formMethods = useForm({
    defaultValues: {
      ...session,
      date: session?.date || new Date().toISOString().split('T')[0],
      analysis_type: session?.analysis_type || (isNew ? SessionAnalysisType.TRAVERSE : session?.analysis_type),
      ...(restoredFormValues || {}),
    },
  });

  // The stash is one-shot: clear it once consumed so a later fresh visit to
  // the new-session page doesn't resurrect stale values. (Cleared in an
  // effect, not inside the lazy initializer — StrictMode double-invokes
  // initializers, and a remove-on-read would lose the value.)
  useEffect(() => {
    if (isNew) {
      clearStash(NEW_SESSION_FORM_KEY);
    }
  }, [isNew]);

  // Set Basic mode defaults when in basic mode for new sessions
  useEffect(() => {
    if (sessionMode === 'basic' && isNew) {
      formMethods.setValue(
        'analysis_type',
        SessionAnalysisType.TRAVERSE,
        { shouldValidate: true },
      );
      // Basic mode is a rider TRAVERSE test: pin config A head/chest slopes manual-0 so
      // the drag-model solve holds body position at 0 (matches the rider radio default).
      formMethods.setValue('head_slope', 0, { shouldValidate: true });
      formMethods.setValue('chest_slope', 0, { shouldValidate: true });
      formMethods.setValue(
        'front_tooth_count', 56, { shouldValidate: true },
      );
      setShowTestTypeWarning(false);
      // Basic mode is always a TRAVERSE session, so the velodrome gate never applies.
      setShowVelodromeWarning(false);
    }
  }, [sessionMode, isNew, formMethods]);

  // Watch for analysis_type changes (drives the velodrome gate in the new-session flow)
  const watchedAnalysisType = formMethods.watch('analysis_type');
  const currentAnalysisType = Number(watchedAnalysisType || session?.analysis_type);

  // Check if velodrome type is selected
  const isVelodromeSession =
    currentAnalysisType === SessionAnalysisType.LOOP_VELODROME_INDOORS ||
    currentAnalysisType === SessionAnalysisType.LOOP_VELODROME_OUTDOORS;

  // Use unsaved changes warning hook
  const { clearUnsavedChanges } = useUnsavedChangesWarning({
    enabled: isNew,
    message: 'You have unsaved changes to this session. Are you sure you want to leave?',
    formMethods: formMethods,
  });

  // Update form when session changes
  useEffect(() => {
    if (!isNew && session) {
      formMethods.reset(session);
    }
  }, [session, isNew, formMethods]);

  // Calculate rider display name
  const getRiderName = (rider_id: string) => (ridersById && ridersById.get(rider_id)?.nickname) || 'None';

  const updateSession = isNew ? useCreateSession() : useUpdateSession(sessionId);

  // The form stash clears itself on mount (above); the route stash lives
  // until the new-session flow ends, so save/cancel own its cleanup.
  const redirectToSessions = () => {
    if (isNew) {
      clearStash(NEW_SESSION_ROUTE_KEY);
    }
    navigate('/sessions');
  };

  const redirectToSession = (session?: any) => {
    navigate(`/sessions/${session.id}`);
  };

  const handleSave = async () => {
    try {
      const formValues = formMethods.getValues();
      const cleanedValues = Object.fromEntries(Object.entries(formValues).filter(([, v]) => v !== ''));

      // Add timezone from browser when creating a new session
      if (isNew) {
        cleanedValues.timezone_initial = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }

      const updatedSession = await updateSession.mutateAsync(cleanedValues);

      // Clear unsaved changes state after successful save
      clearUnsavedChanges();
      if (isNew) {
        // Don't leak this session's route/gates stash into the next new session
        clearStash(NEW_SESSION_ROUTE_KEY);
      }
      redirectToSession(updatedSession);
    } catch (e) {
      console.error(e);
      toast.error('Unknown error saving session');
    }
  };

  const SessionNavButtons = () => {
    if (isNew) {
      return null;
    }

    // Extract current tab from URL path
    const pathParts = location.pathname.split('/');
    const currentTab = pathParts[3] || 'config'; // Default to 'config' if no tab specified
    const validTabs = ['config', 'results', 'charts', 'tyre-plots'];

    // Helper to check if a session supports tyre-plots tab
    const sessionSupportsTyrePlots = (targetSessionId: string) => {
      const targetSession = allSessions?.find((s) => s.id === targetSessionId);
      return (
        targetSession?.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_REFERENCE ||
        targetSession?.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON
      );
    };

    // Helper to get the navigation path for a target session
    const getNavigationPath = (targetSessionId: string) => {
      let tab = currentTab;
      // If current tab is tyre-plots but target doesn't support it, fall back to config
      if (tab === 'tyre-plots' && !sessionSupportsTyrePlots(targetSessionId)) {
        tab = 'config';
      }
      // Only append tab if it's a valid tab
      if (validTabs.includes(tab)) {
        return `/sessions/${targetSessionId}/${tab}`;
      }
      return `/sessions/${targetSessionId}/config`;
    };

    return (
      <SessionNavigation
        previousSessionId={previousSessionId}
        nextSessionId={nextSessionId}
        getNavigationPath={getNavigationPath}
      />
    );
  };

  const SessionModeToggle = () => {
    if (!isNew || !isMissingConfigs) return null;
    return (
      <div className={
        'cr-mb-4 cr-flex cr-flex-col cr-rounded-lg '
        + 'cr-bg-white cr-p-2 cr-text-center cr-shadow-md '
        + 'md:cr-w-[640px] md:cr-text-left'
      }>
        <FormEntry label="Options">
          <div className="cr-flex cr-flex-row cr-gap-4">
            <label className={
              'cr-flex cr-cursor-pointer cr-items-center'
            }>
              <input
                type="radio"
                name="sessionMode"
                value="basic"
                checked={sessionMode === 'basic'}
                onChange={() => setSessionMode('basic')}
                className="cr-mr-2"
              />
              <span>Basic</span>
            </label>
            <label className={
              'cr-flex cr-cursor-pointer cr-items-center'
            }>
              <input
                type="radio"
                name="sessionMode"
                value="advanced"
                checked={sessionMode === 'advanced'}
                onChange={() => setSessionMode('advanced')}
                className="cr-mr-2"
              />
              <span>Advanced</span>
            </label>
          </div>
        </FormEntry>
      </div>
    );
  };

  const ButtonActions = () => {
    if (isNew) {
      return (
        <>
          <ButtonList>
            <Button
              onClick={formMethods.handleSubmit(handleSave)}
              icon={<HiOutlineCheckCircle className={'cr-size-7'} />}
              disabled={updateSession.isPending || showRiderWarning || showTestTypeWarning || showVelodromeWarning}
            >
              Save
            </Button>
            <Button
              onClick={redirectToSessions}
              icon={<HiOutlineXMark className={'cr-size-7'} />}
              disabled={updateSession.isPending}
            >
              Cancel
            </Button>
          </ButtonList>
          {/* Explain why Save is disabled for a velodrome session with no route chosen yet. */}
          {showVelodromeWarning && (
            <div className="cr-mt-2 cr-flex cr-items-center cr-gap-1.5 cr-text-sm cr-text-orange-600">
              <HiOutlineExclamationTriangle className="cr-h-4 cr-w-4 cr-flex-shrink-0" />
              <span>Select or create a velodrome above to continue.</span>
            </div>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <FormProvider {...formMethods}>
      <div className="cr-w-full">
        {/* Header Row: Header + Underline + Back Link */}
        <SectionHeader
          title={isNew ? 'New Session' : 'Session Details'}
          isLoading={isSessionFetching || updateSession.isPending}
          backLink={headerBackLink}
          className="!cr-mb-6"
         
        />
        <div>
          <Skeleton isPending={isSessionPending || isRidersPending}>
            <ButtonActions />
            {/* Below lg the session pager sits above the cards, right-aligned,
                rather than stealing a column of width beside them. */}
            <div className="cr-flex cr-flex-col cr-gap-2 lg:cr-flex-row lg:cr-items-start lg:cr-gap-4">
              <div className="cr-order-2 cr-flex-1 lg:cr-order-none">
                <SessionCards
                  formMethods={formMethods}
                  ridersById={ridersById}
                  getRiderName={getRiderName}
                  isNew={isNew}
                  updateSession={updateSession}
                  showRiderWarning={showRiderWarning}
                  advancedOptions={
                    <SessionOptions
                      sessionId={sessionId}
                      session={session}
                      updateSession={updateSession}
                      invalidateSession={invalidateSession}
                      clearUnsavedChanges={clearUnsavedChanges}
                    />
                  }
                  session={session}
                  onSummaryUpdate={invalidateSession}
                />
              </div>
              <div className="cr-order-1 cr-self-end lg:cr-order-none lg:cr-self-auto">
                <SessionNavButtons />
              </div>
            </div>
            {!isSessionFetching && isMissingConfigs && (
              <SessionModeToggle />
            )}
            {!isSessionFetching && isMissingConfigs
              && sessionMode === 'advanced' && (
              <NewSessionCalibrationParameters
                onTestTypeWarningChange={
                  setShowTestTypeWarning
                }
                onVelodromeWarningChange={
                  setShowVelodromeWarning
                }
              />
            )}
            {!isSessionFetching && isMissingConfigs && (
              <div className={
                sessionMode === 'basic'
                  ? 'cr-pb-48 md:cr-pb-0'
                  : 'cr-pb-96 md:cr-pb-0'
              } />
            )}
            {!isSessionFetching && isMissingConfigs && (
              <div className={
                'cr-fixed cr-bottom-0 cr-left-0 '
                + 'cr-right-0 cr-z-50 cr-max-h-[50vh] '
                + 'cr-overflow-y-auto cr-border-t '
                + 'cr-border-gray-200 cr-bg-gray-50 '
                + 'cr-px-4 cr-pb-4 cr-pt-4 '
                + 'md:cr-relative md:cr-max-h-none '
                + 'md:cr-border-0 md:cr-bg-transparent '
                + 'md:cr-p-0'
              }>
                <NewSessionCalibrationConfig
                  ridersById={ridersById || new Map()}
                  onRiderWarningChange={
                    setShowRiderWarning
                  }
                  basicMode={sessionMode === 'basic'}
                />
              </div>
            )}
            {!isSessionFetching && isMissingConfigs
              && sessionMode === 'advanced' && !isVelodromeSession && (
              <NewSessionRouteSelector />
            )}
            {!isSessionFetching && isMissingConfigs && (
              <ButtonActions />
            )}
          </Skeleton>
        </div>
      </div>
    </FormProvider>
  );
}
